import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import {
  Landmark, HeartPulse, Briefcase, Home, GraduationCap,
  Users, Brain, Accessibility, ShieldCheck, UsersRound,
  Mic, X, Check, AlertCircle, Sparkles,
} from "lucide-react";

/**
 * WhereDiagram — animated infographic component.
 * Two variants share the same 10-pillar layout language:
 *   - variant="problem"  → red X badges, "Veterans fall through the gaps"
 *   - variant="solution" → gold ✓ badges, "One warm handoff. One outcome."
 */
const PILLARS = [
  { key: "va",         label: "VA Benefits",         desc: "Eligibility, claims, and services",           side: "left",  row: 0, tone: "#0F2138", Icon: Landmark },
  { key: "healthcare", label: "Healthcare",          desc: "Medical care, mental health, and treatment",  side: "left",  row: 1, tone: "#2E7D32", Icon: HeartPulse },
  { key: "employment", label: "Employment",          desc: "Job placement, training, and career services",side: "left",  row: 2, tone: "#6A1B9A", Icon: Briefcase },
  { key: "housing",    label: "Housing",             desc: "Shelter, stability, and supportive housing",   side: "left",  row: 3, tone: "#0277BD", Icon: Home },
  { key: "education",  label: "Education",           desc: "Tuition assistance, schools, and certifications",side: "left",row: 4, tone: "#8E1B36", Icon: GraduationCap },
  { key: "family",     label: "Family Support",      desc: "Family programs, caregiver resources, and guidance", side: "right", row: 0, tone: "#C6934C", Icon: Users },
  { key: "mental",     label: "Mental Wellness",     desc: "Counseling, peer support, and well-being",     side: "right", row: 1, tone: "#D97706", Icon: Brain },
  { key: "disability", label: "Disability Services", desc: "Evaluations, adaptive resources, and advocacy",side: "right", row: 2, tone: "#B7410E", Icon: Accessibility },
  { key: "legal",      label: "Legal & Identity",    desc: "Legal aid, discharge upgrades, and ID change support", side: "right", row: 3, tone: "#1E40AF", Icon: ShieldCheck },
  { key: "community",  label: "Community",           desc: "Local nonprofits, faith groups, and peer networks", side: "right", row: 4, tone: "#2E7D32", Icon: UsersRound },
];

export default function WhereDiagram({ variant = "problem" }) {
  const wrapRef = useRef(null);
  const inView = useInView(wrapRef, { once: true, amount: 0.2 });
  const isProblem = variant === "problem";

  return (
    <section
      ref={wrapRef}
      className={`relative overflow-hidden ${isProblem ? "bg-white" : "bg-navy-deep text-cream"}`}
      data-testid={`where-diagram-${variant}`}
    >
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-center gap-4"
          >
            <span className={`h-[2px] w-16 ${isProblem ? "bg-gold" : "bg-gold"}`} />
            <p className={`eyebrow ${isProblem ? "text-navy" : "text-gold"}`}>
              {isProblem ? "Where We Are" : "Where WHO Fits"}
            </p>
            <span className={`h-[2px] w-16 ${isProblem ? "bg-gold" : "bg-gold"}`} />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 14 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1, duration: 0.6 }}
            className={`mt-5 font-display font-black tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl ${isProblem ? "text-navy" : "text-cream"}`}
          >
            {isProblem ? (
              <>Programs exist. <span className="text-gold-deep">Accountability between them does not.</span></>
            ) : (
              <>WHO Guides connect them. <span className="text-gold">Accountability lives between.</span></>
            )}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.25, duration: 0.6 }}
            className={`mt-5 text-base lg:text-lg leading-relaxed ${isProblem ? "text-slateSoft" : "text-cream/70"} max-w-3xl mx-auto`}
          >
            {isProblem
              ? "Transition is not a single event. It is an extended process across employment, benefits, healthcare, disability, housing, education, family, mental wellness, and identity — each managed by a different organization with different rules and different data."
              : "The same ten programs, now on one Relationship Operating System™. One real WHO Guide orchestrates nine specialist AI agents so every referral is warm, every connection is tracked, and every loop closes."}
          </motion.p>
        </div>

        {/* Diagram */}
        <div className="mt-14 relative">
          {/* Ambient decorative connector lines — CSS-based, no absolute positioning */}
          <div
            aria-hidden
            className={`absolute inset-x-0 top-1/2 -translate-y-1/2 h-[70%] pointer-events-none hidden md:block ${isProblem ? "opacity-40" : "opacity-60"}`}
            style={{
              background: isProblem
                ? "radial-gradient(ellipse at center, transparent 25%, rgba(15,33,56,0.06) 60%, transparent 75%)"
                : "radial-gradient(ellipse at center, transparent 20%, rgba(198,147,76,0.15) 55%, transparent 75%)",
            }}
          />

          {/* Two-column grid of pillars + center card */}
          <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-10 items-center">
            {/* Left column */}
            <div className="space-y-4 md:space-y-3">
              {PILLARS.filter((p) => p.side === "left").map((p, i) => (
                <PillarChip key={p.key} p={p} inView={inView} index={i} align="right" variant={variant} />
              ))}
            </div>

            {/* Center */}
            <CenterCard variant={variant} inView={inView} />

            {/* Right column */}
            <div className="space-y-4 md:space-y-3">
              {PILLARS.filter((p) => p.side === "right").map((p, i) => (
                <PillarChip key={p.key} p={p} inView={inView} index={i + 5} align="left" variant={variant} />
              ))}
            </div>
          </div>
        </div>

        {/* Bottom banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 2.1, duration: 0.6 }}
          className={`mt-14 lg:mt-16 rounded-lg p-6 lg:p-7 flex items-start gap-4 ${
            isProblem ? "bg-navy-deep text-cream" : "bg-gold text-navy"
          }`}
        >
          {isProblem ? (
            <AlertCircle className="w-7 h-7 text-gold shrink-0 mt-0.5" />
          ) : (
            <Sparkles className="w-7 h-7 text-navy shrink-0 mt-0.5" />
          )}
          <div>
            <p className={`font-display font-bold text-lg lg:text-xl ${isProblem ? "text-cream" : "text-navy"}`}>
              {isProblem
                ? "Different systems. Different rules. Different data."
                : "One system. One relationship. One outcome."}
            </p>
            <p className={`mt-1 text-sm lg:text-base ${isProblem ? "text-gold-light" : "text-navy-800"}`}>
              {isProblem
                ? "No single point of accountability. No way to confirm connection. No shared outcomes."
                : "One accountable Guide. One warm handoff. One shared record — for the person and every partner."}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function PillarChip({ p, inView, index, align, variant }) {
  const isProblem = variant === "problem";
  const dir = align === "left" ? 30 : -30;
  const Mark = isProblem ? X : Check;
  const markBg = isProblem ? "#0F2138" : "#C6934C";
  const markFg = isProblem ? "#FFFFFF" : "#0F2138";
  return (
    <motion.div
      initial={{ opacity: 0, x: dir }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ delay: 0.35 + index * 0.06, duration: 0.5, ease: "easeOut" }}
      className={`flex items-start gap-3 md:gap-4 ${align === "left" ? "md:flex-row" : "md:flex-row-reverse md:text-right"}`}
      data-testid={`pillar-${variant}-${p.key}`}
    >
      <div className="relative shrink-0">
        <div
          className={`w-14 h-14 rounded-full flex items-center justify-center border-2 ${
            isProblem ? "border-white/60" : "border-cream/40"
          } shadow-md`}
          style={{ backgroundColor: p.tone }}
        >
          <p.Icon className="w-6 h-6 text-white" strokeWidth={2.4} />
        </div>
        {/* Status badge — X for problem, ✓ for solution */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={inView ? { scale: 1, opacity: 1 } : {}}
          transition={{ delay: 0.7 + index * 0.06, duration: 0.35, type: "spring", stiffness: 260, damping: 18 }}
          className={`absolute -bottom-1 ${align === "left" ? "-right-1" : "-left-1"} w-5 h-5 rounded-full flex items-center justify-center shadow-md`}
          style={{ backgroundColor: markBg, color: markFg }}
          aria-hidden
        >
          <Mark className="w-3 h-3" strokeWidth={3.5} />
        </motion.div>
      </div>
      <div className="flex-1">
        <p className={`font-display font-bold text-[13px] uppercase tracking-widest ${isProblem ? "text-navy" : "text-cream"}`}>
          {p.label}
        </p>
        <p className={`mt-0.5 text-xs leading-relaxed ${isProblem ? "text-slateSoft" : "text-cream/70"}`}>
          {p.desc}
        </p>
      </div>
    </motion.div>
  );
}

function CenterCard({ variant, inView }) {
  const isProblem = variant === "problem";
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ delay: 1.6, duration: 0.55, type: "spring", stiffness: 180, damping: 22 }}
      className={`relative w-[240px] lg:w-[280px] p-6 rounded-2xl text-center ${
        isProblem
          ? "bg-gradient-to-br from-white to-cream-panel border border-borderSubtle shadow-lg text-navy"
          : "bg-gradient-to-br from-gold/25 to-gold/10 border border-gold/50 shadow-[0_0_50px_-10px_rgba(198,147,76,0.5)] text-cream"
      }`}
      data-testid={`center-card-${variant}`}
    >
      {isProblem ? (
        <>
          <div className="space-y-1.5 font-display font-bold text-navy text-sm lg:text-base">
            <p>No shared system.</p>
            <p>No accountability.</p>
            <p>No confirmation.</p>
            <p>No visibility.</p>
          </div>
          <div className="my-4 h-[1px] bg-borderSubtle" />
          <p className="font-display font-black text-gold-deep text-[13px] uppercase tracking-widest leading-tight">
            Veterans fall<br />through the gaps.
          </p>
        </>
      ) : (
        <>
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gold text-navy mb-3">
            <Mic className="w-6 h-6" strokeWidth={2.4} />
          </div>
          <p className="font-display font-black text-cream text-lg leading-tight">
            WHO Guides™
          </p>
          <p className="mt-1 text-[10px] uppercase tracking-widest text-gold-light font-bold">
            Agentic · Human-in-the-loop
          </p>
          <div className="my-4 h-[1px] bg-gold/25" />
          <div className="space-y-1 font-display font-bold text-cream text-sm">
            <p>One shared record.</p>
            <p>One accountable Guide.</p>
            <p>One warm handoff.</p>
            <p>One outcome.</p>
          </div>
        </>
      )}
    </motion.div>
  );
}

// Named exports for convenience
export function WhereWeAreDiagram() { return <WhereDiagram variant="problem" />; }
export function WhereWhoFitsDiagram() { return <WhereDiagram variant="solution" />; }
